import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { Form, Col, Row } from "react-bootstrap";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { ReportDefinitionFormDto, ReportDefinitionFormSelectorsDto } from "../../types";
import { BiannuallyFrequencyEnum, MonthlyFrequencyEnum, QuarterlyFrequencyEnum, ReportContentDeliveryTypeEnum, ReportDefinitionStatusEnum, ReportExecutionModeEnum, ReportFrequencyTypeEnum } from "../../types/enums";
import AppMultiSelect from "../../../../components/AppInput/AppMultiSelect";
import AppHierarchicalMultiSelect from "../../../../components/AppHierarchicalMultiSelect/AppHierarchicalMultiSelect";
import { setForm } from "../../../../redux/reportDefinitionSlice";
import { format } from 'date-fns';

interface ReportDefinitionDetailsTabProp {
    selectors: ReportDefinitionFormSelectorsDto;
    updateField: (field: keyof ReportDefinitionFormDto, value: ReportDefinitionFormDto[keyof ReportDefinitionFormDto]) => void;
}

export default function ReportDefinitionDetailsTab({
    selectors,
    updateField
}: ReportDefinitionDetailsTabProp) {
    const { t } = useTranslation(["reportDefinition", "common"]);
    const dispatch = useDispatch();
    
    const formData = useSelector((state: RootState) => state.reportDefinitionForm.data);
    const errors = useSelector((state: RootState) => state.reportDefinitionForm.errors);
    
    return (
        <Form>
            <Row className="mt-1">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.id")} :</Form.Label>
                        <Form.Control
                            type="number"
                            value={formData.id ?? ""}
                            disabled
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>{t("common:status")}</Form.Label>
                        <Form.Select
                            as="select"
                            value={formData.status ?? 0}
                            onChange={(e) => updateField("status", parseInt(e.target.value))}
                        >
                            <option value=""></option>
                            {Object.values(ReportDefinitionStatusEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                const value = ReportDefinitionStatusEnum[name as keyof typeof ReportDefinitionStatusEnum];
                                return (<option key={value} value={value}>
                                    {t(`reportDefinition:form.status.${name}`)}
                                </option>)
                            })}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={12}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.title")} * :</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.title}
                            isInvalid={!!errors.title}
                            onChange={(e) => updateField("title", e.target.value)}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.title}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            {
                formData.executionMode == ReportExecutionModeEnum.Scheduled && 
            <Row className="mt-1">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>{t("reportDefinition:form.fields.frequencyType")}</Form.Label>
                        <Form.Select
                            as="select"
                            value={formData.frequencyType ?? ""}
                            onChange={(e) => updateField("frequencyType", parseInt(e.target.value))}
                            isInvalid={!!errors.frequencyType}
                        >
                            <option value=""></option>
                            {Object.values(ReportFrequencyTypeEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                const value = ReportFrequencyTypeEnum[name as keyof typeof ReportFrequencyTypeEnum];
                                return (<option key={value} value={value}>
                                    {t(`reportDefinition:form.frequencyType.${name}`)} 
                                </option>)
                            })}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.frequencyType}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                {
                    formData.frequencyType == ReportFrequencyTypeEnum.BetweenDates && (<>
                        <Col md={4}>
                            <Form.Group>
                                <InputDatePicker
                                    value={formData.startDate ?? null}
                                    label={t("common:form.fields.startDate")}
                                    onChange={(date) => updateField("startDate", date ? format(date, 'yyyy-MM-dd') : undefined)}
                                    hasError={!!errors.startDate}
                                    errorMessage={errors.startDate}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <InputDatePicker
                                    value={formData.endDate ?? null}
                                    label={t("common:form.fields.endDate")}
                                    onChange={(date) => updateField("endDate", date ? format(date, 'yyyy-MM-dd') : undefined)}
                                    hasError={!!errors.endDate}
                                    errorMessage={errors.endDate}
                                />
                            </Form.Group>
                        </Col>
                    </>)
                }
                {
                    formData.frequencyType == ReportFrequencyTypeEnum.Monthly && (
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>{t("reportDefinition:form.fields.month")}</Form.Label>
                                <Form.Select
                                    name="month"
                                    value={formData.month}
                                    onChange={(e) => updateField('month', Number(e.target.value))}
                                    isInvalid={!!errors.month}
                                >
                                    <option value=""></option>
                                    {Object.values(MonthlyFrequencyEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                        const value = MonthlyFrequencyEnum[name as keyof typeof MonthlyFrequencyEnum];
                                        return (<option key={value} value={value}>
                                            {t(`reportDefinition:form.month.${name}`)} 
                                        </option>)
                                    })}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.month}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    )
                }
                {
                    formData.frequencyType == ReportFrequencyTypeEnum.Quarterly && (
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>{t("reportDefinition:form.fields.quarter")}</Form.Label>
                                <Form.Select
                                    name="quarter"
                                    value={formData.quarter}
                                    onChange={(e) => updateField('quarter', Number(e.target.value))}
                                    isInvalid={!!errors.quarter}
                                >
                                    <option value=""></option>
                                    {Object.values(QuarterlyFrequencyEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                        const value = QuarterlyFrequencyEnum[name as keyof typeof QuarterlyFrequencyEnum];
                                        return (<option key={value} value={value}>
                                            {name} 
                                        </option>)
                                    })}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.quarter}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    )
                }
                {
                    formData.frequencyType == ReportFrequencyTypeEnum.Biannually && (
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>{t("reportDefinition:form.fields.semester")}</Form.Label>
                                <Form.Select
                                    name="semester"
                                    value={formData.semester}
                                    onChange={(e) => updateField('semester', Number(e.target.value))}
                                    isInvalid={!!errors.semester}
                                >
                                    <option value=""></option>
                                    {Object.values(BiannuallyFrequencyEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                        const value = BiannuallyFrequencyEnum[name as keyof typeof BiannuallyFrequencyEnum];
                                        return (<option key={value} value={value}>
                                            {name} 
                                        </option>)
                                    })}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.semester}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    )
                }
                {
                    formData.frequencyType && formData.frequencyType != ReportFrequencyTypeEnum.BetweenDates && !formData.isPeriodical && (
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>{t("reportDefinition:form.fields.year")}</Form.Label>
                                <Form.Select
                                    name="year"
                                    value={formData.year}
                                    onChange={(e) => updateField('year', Number(e.target.value))}
                                    isInvalid={!!errors.year}
                                >
                                    <option value=""></option>
                                    {Array.from({ length: 10 }, (_, index) => {
                                        const year = new Date().getFullYear() - index;

                                        return (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.year}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    )
                }
            </Row>
            }
            <Row className="mt-1">
                <Col md={12} className="d-flex gap-4">
                    {(Object.keys(ReportExecutionModeEnum) as Array<keyof typeof ReportExecutionModeEnum>)
                        .filter((key) => isNaN(Number(key)))
                        .map((executionMode) => (
                            <Form.Check
                                key={executionMode}
                                type="radio"
                                label={t(`reportDefinition:form.executionMode.${executionMode}`)}
                                value={executionMode}
                                onChange={() => {
                                    if (ReportExecutionModeEnum[executionMode] == ReportExecutionModeEnum.Now) {
                                        dispatch(
                                            setForm(
                                                {
                                                    executionMode: ReportExecutionModeEnum[executionMode],
                                                    sendingDate: new Date(),
                                                    frequencyType: null,
                                                    startDate: null,
                                                    endDate: null
                                                }
                                            )
                                        );
                                    } else {
                                        updateField('executionMode', ReportExecutionModeEnum[executionMode])
                                    }
                                }}
                                className="mb-2"
                                checked={ReportExecutionModeEnum[executionMode] == formData.executionMode}
                            />
                        ))}
                    {
                        formData.executionMode == ReportExecutionModeEnum.Scheduled && formData.frequencyType != ReportFrequencyTypeEnum.BetweenDates &&
                            <Form.Check
                                type="checkbox"
                                id="report-definition-is-periodical"
                                label={t("reportDefinition:form.fields.periodical")}
                                checked={formData.isPeriodical}
                                onChange={(e) => updateField('isPeriodical', e.target.checked)}
                            />
                    }       
                </Col>
            </Row>
            <Row className="mt-1">
                <Col md={4}>
                    <Form.Group>
                        <InputDatePicker
                            value={formData.sendingDate ?? null}
                            label={t("reportDefinition:form.fields.sendingDate") + " *"}
                            onChange={(date) => updateField("sendingDate", date ? format(date, 'yyyy-MM-dd') : undefined)}
                            hasError={!!errors.sendingDate}
                            errorMessage={errors.sendingDate}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col md={12} className="d-flex gap-4">
                    {(Object.keys(ReportContentDeliveryTypeEnum) as Array<keyof typeof ReportContentDeliveryTypeEnum>)
                        .filter((key) => isNaN(Number(key)))
                        .map((deliveryType) => (
                            <Form.Check
                                key={deliveryType}
                                type="radio"
                                label={t(`reportDefinition:form.deliveryType.${deliveryType}`)}
                                value={deliveryType}
                                onChange={() => updateField('contentDeliveryType', ReportContentDeliveryTypeEnum[deliveryType])}
                                className="mb-2"
                                checked={ReportContentDeliveryTypeEnum[deliveryType] == formData.contentDeliveryType}
                            />
                        ))}
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("reportDefinition:form.fields.requestTypes")} :</Form.Label>
                        <AppMultiSelect
                            options={selectors.requestOptions ?? []}
                            selectedValues={formData.selectedRequestTypes ?? []}
                            onChange={(values) => updateField("selectedRequestTypes", values)}
                            isInvalid={!!errors.selectedRequestTypes}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedRequestTypes}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("reportDefinition:form.fields.functionalAreas")} :</Form.Label>
                        <AppMultiSelect
                            options={selectors.functionalAreaOptions ?? []}
                            selectedValues={formData.selectedFunctionalAreas ?? []}
                            onChange={(values) => updateField("selectedFunctionalAreas", values)}
                            isInvalid={!!errors.selectedFunctionalAreas}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedFunctionalAreas}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("reportDefinition:form.fields.services")} :</Form.Label>
                        <AppMultiSelect
                            options={selectors.serviceOptions ?? []}
                            selectedValues={formData.selectedServiceIndicators ?? []}
                            onChange={(values) => updateField("selectedServiceIndicators", values)}
                            isInvalid={!!errors.selectedServiceIndicators}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedServiceIndicators}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("reportDefinition:form.fields.organizationalStructures")} :</Form.Label>
                        <AppHierarchicalMultiSelect
                            options={selectors.organizationalStructureOptions ?? []}
                            selectedValues={formData.selectedOrganizationalStructures ?? []}
                            onChange={(values) => updateField("selectedOrganizationalStructures", values)}
                            isInvalid={!!errors.selectedOrganizationalStructures}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedOrganizationalStructures}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={6}>
                    <Form.Check
                        type="checkbox"
                        id="report-definition-is-visible-in-service"
                        label={t("reportDefinition:form.fields.isVisibleInService")}
                        checked={formData.isVisibleInService}
                        onChange={(e) => updateField('isVisibleInService', e.target.checked)}
                    />
                </Col>
                <Col xs={6}>
                    <Form.Check
                        type="checkbox"
                        id="report-definition-is-visible-in-organizational-structure"
                        label={t("reportDefinition:form.fields.isVisibleInOrganizationalStructure")}
                        checked={formData.isVisibleInOrganizationalStructure}
                        onChange={(e) => updateField('isVisibleInOrganizationalStructure', e.target.checked)}
                    />
                </Col>
            </Row>
        </Form>
    );
}